import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { RotateCcw } from 'lucide-react';
import { getIconComponent } from './IconPicker';
import { handleModifierAwareNavigate } from '../utils/app-navigation';
import { CardMetaText } from './CardMetaText';
import { ProjectPanelLayout } from './ProjectPanelLayout';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { notePreview } from '../utils/note-utils';
import {
  resolveTrashTypeFilter,
  TrashTypeFilter,
  TRASH_TYPE_FILTER_ALL,
} from './TrashTypeFilter';

interface DocumentItem {
  id: string;
  title: string;
  icon: string | null;
  projectId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TrashedNoteItem {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

const NOTE_PREVIEW_MAX_LEN = 200;

export const ProjectTrash: React.FC = () => {
  const params = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useLocale();
  const { showToast, showErrorToast } = useToast();
  const { canEditProject } = useProjectAccess();
  const [trashedDocuments, setTrashedDocuments] = useState<DocumentItem[]>([]);
  const [trashedNotes, setTrashedNotes] = useState<TrashedNoteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState(TRASH_TYPE_FILTER_ALL);

  const fetchTrash = useCallback(async (): Promise<void> => {
    if (!token || !params.projectId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [documentsResponse, notesResponse] = await Promise.all([
        fetch(`${API_BASE}/api/documents?trashed=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/api/projects/${params.projectId}/notes?trashed=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (documentsResponse.ok) {
        const data = await documentsResponse.json();
        const documents = (data.documents || []).filter(
          (doc: DocumentItem) => doc.projectId === params.projectId
        );
        setTrashedDocuments(documents);
      } else {
        setTrashedDocuments([]);
      }

      if (notesResponse.ok) {
        const data = await notesResponse.json();
        setTrashedNotes(data.notes || []);
      } else {
        setTrashedNotes([]);
      }
    } catch (err) {
      console.error('Error fetching project trash:', err);
      setTrashedDocuments([]);
      setTrashedNotes([]);
    } finally {
      setLoading(false);
    }
  }, [token, params.projectId]);

  useEffect(() => {
    void fetchTrash();
  }, [fetchTrash]);

  useEffect(() => {
    const refreshIfSameProject = (event: Event): void => {
      const customEvent = event as CustomEvent<{ projectId: string }>;
      if (customEvent.detail.projectId === params.projectId) {
        void fetchTrash();
      }
    };

    window.addEventListener('document-deleted', refreshIfSameProject);
    window.addEventListener('note-deleted', refreshIfSameProject);

    return () => {
      window.removeEventListener('document-deleted', refreshIfSameProject);
      window.removeEventListener('note-deleted', refreshIfSameProject);
    };
  }, [fetchTrash, params.projectId]);

  const handleRestoreDocument = async (documentId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setRestoring(documentId);
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/restore`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore document');
      }

      setTrashedDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      showToast(t('document_restored') || 'Document restored');

      window.dispatchEvent(new CustomEvent('document-restored', {
        detail: { documentId, projectId: params.projectId },
      }));
    } catch (err) {
      console.error('Error restoring document:', err);
      showErrorToast(t('failed_to_restore_document') || 'Failed to restore document');
    } finally {
      setRestoring(null);
    }
  };

  const handleRestoreNote = async (noteId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setRestoring(noteId);
      const response = await fetch(`${API_BASE}/api/notes/${noteId}/restore`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore note');
      }

      setTrashedNotes((prev) => prev.filter((note) => note.id !== noteId));
      showToast(t('note_restored') || 'Note restored');

      window.dispatchEvent(new CustomEvent('note-restored', {
        detail: { noteId, projectId: params.projectId },
      }));
    } catch (err) {
      console.error('Error restoring note:', err);
      showErrorToast(t('failed_to_restore_note') || 'Failed to restore note');
    } finally {
      setRestoring(null);
    }
  };

  const isEmpty = trashedDocuments.length === 0 && trashedNotes.length === 0;
  const trashTypeOptions = useMemo(
    () => [
      { id: 'documents', label: t('Documents'), count: trashedDocuments.length },
      { id: 'notes', label: t('Notes'), count: trashedNotes.length },
    ],
    [trashedDocuments.length, trashedNotes.length, t]
  );
  const activeTypeFilter = resolveTrashTypeFilter(typeFilter, trashTypeOptions);
  const showDocuments =
    activeTypeFilter === TRASH_TYPE_FILTER_ALL || activeTypeFilter === 'documents';
  const showNotes = activeTypeFilter === TRASH_TYPE_FILTER_ALL || activeTypeFilter === 'notes';

  return (
    <ProjectPanelLayout title={t('trash')} titleIcon={PROJECT_SECTION_ICONS.trash} loading={loading}>
      {isEmpty ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>
          <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {t('no_trashed_documents')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <TrashTypeFilter
            value={activeTypeFilter}
            onChange={setTypeFilter}
            options={trashTypeOptions}
          />
          {showDocuments && trashedDocuments.map((doc) => {
            const DocIcon = doc.icon ? getIconComponent(doc.icon) : null;
            return (
              <Card
                key={doc.id}
                className="relative border-0 shadow-none rounded-[10px] cursor-pointer transition-all bg-card hover:bg-muted"
                style={{ border: '1px solid var(--border)' }}
                onClick={(e) => {
                  handleModifierAwareNavigate(
                    e,
                    navigate,
                    `/projects/${params.projectId}/documents/${doc.id}`
                  );
                }}
                onAuxClick={(e) => {
                  handleModifierAwareNavigate(
                    e,
                    navigate,
                    `/projects/${params.projectId}/documents/${doc.id}`
                  );
                }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}
                >
                  {DocIcon ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--foreground)',
                        minWidth: '48px',
                        maxWidth: '48px',
                      }}
                    >
                      <DocIcon size={48} />
                    </div>
                  ) : (
                    <div
                      style={{
                        minWidth: '48px',
                        maxWidth: '48px',
                        height: '48px',
                        backgroundColor: 'var(--muted)',
                        border: '1px dashed var(--border)',
                        borderRadius: '4px',
                      }}
                    />
                  )}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', lineHeight: '1.5' }}>
                      {doc.title}
                    </h3>
                    <CardMetaText>
                      {new Date(doc.createdAt).toLocaleDateString()} · {t('last_update')}: {new Date(doc.updatedAt).toLocaleDateString()}
                    </CardMetaText>
                  </div>
                  {canEditProject && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleRestoreDocument(doc.id);
                    }}
                    disabled={restoring === doc.id}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {restoring === doc.id ? (t('versionHistory.restoring') || 'Restoring...') : t('restore')}
                  </Button>
                  )}
                </div>
              </Card>
            );
          })}
          {showNotes && trashedNotes.map((note) => (
            <Card
              key={note.id}
              className="relative border-0 shadow-none rounded-[10px] transition-all bg-card"
              style={{ border: '1px solid var(--border)' }}
            >
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {notePreview(note.content, NOTE_PREVIEW_MAX_LEN) || t('no_content') || '(No content)'}
                  </p>
                  <CardMetaText className="mt-2">
                    {new Date(note.updatedAt).toLocaleDateString()}
                  </CardMetaText>
                </div>
                {canEditProject && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void handleRestoreNote(note.id);
                    }}
                    disabled={restoring === note.id}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {restoring === note.id ? (t('versionHistory.restoring') || 'Restoring...') : t('restore')}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </ProjectPanelLayout>
  );
};
